import { useCallback, useEffect, useState } from 'react'
import { runtimeApi } from '@/app/services/runtimeApi'
import { logger } from '@/utils/logger'
import { alertAsync } from '@/shared/dialogs'
import { printErrorMessage } from './printErrors'
import type { LayoutPickMenuState, PrintCommand } from './types'
import type { FilledSpreadsheetModel } from './spreadsheet/types'
import type { PrintWindowLanguage } from './openPrintWindow'

export { printErrorMessage }

export type UsePrintCommandsArgs = {
  connectionId: string | undefined
  objectName: string
  /** Form passes the saved record id; list omits (object-level commands). */
  recordId?: string | null
}

function normalizeCommands(raw: unknown): PrintCommand[] {
  if (Array.isArray(raw)) return raw as PrintCommand[]
  if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown }).data)) {
    return (raw as { data: PrintCommand[] }).data
  }
  return []
}

function asPrintLanguages(raw: unknown): PrintWindowLanguage[] {
  if (!Array.isArray(raw)) return []
  const out: PrintWindowLanguage[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const o = item as Record<string, unknown>
    if (typeof o.code !== 'string' || typeof o.name !== 'string') continue
    out.push({ code: o.code, name: o.name, isDefault: Boolean(o.isDefault) })
  }
  return out
}

function asPrintLocale(raw: unknown): string | null {
  return typeof raw === 'string' && raw.trim() ? raw : null
}

export function normalizePrintPayload(raw: unknown): {
  model: unknown
  title: string
  locale: string | null
  languages: PrintWindowLanguage[]
} | null {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>
  if ('model' in obj) {
    return {
      model: obj.model,
      title: String(obj.title ?? ''),
      locale: asPrintLocale(obj.locale),
      languages: asPrintLanguages(obj.languages),
    }
  }
  const nested = obj.data
  if (nested && typeof nested === 'object' && 'model' in (nested as object)) {
    const d = nested as Record<string, unknown>
    return {
      model: d.model,
      title: String(d.title ?? ''),
      locale: asPrintLocale(d.locale),
      languages: asPrintLanguages(d.languages),
    }
  }
  return null
}

export function usePrintCommands({
  connectionId,
  objectName,
  recordId,
}: UsePrintCommandsArgs): {
  commands: PrintCommand[]
  hasPrintLayouts: boolean
  printLoading: boolean
  runPrint: (record: Record<string, unknown>, command: PrintCommand) => Promise<void>
  openLayoutChoice: (
    anchorEl: HTMLElement,
    record: Record<string, unknown>,
    mode: 'print' | 'pdf',
  ) => void
  layoutPickMenu: LayoutPickMenuState | null
  closeLayoutPickMenu: () => void
} {
  const [commands, setCommands] = useState<PrintCommand[]>([])
  const [printLoading, setPrintLoading] = useState(false)
  const [layoutPickMenu, setLayoutPickMenu] = useState<LayoutPickMenuState | null>(null)

  useEffect(() => {
    if (!connectionId || !objectName) {
      setCommands([])
      return
    }
    let cancelled = false
    runtimeApi
      .printCommands(connectionId, objectName, recordId)
      .then((data) => {
        if (!cancelled) setCommands(normalizeCommands(data))
      })
      .catch((e) => {
        logger.error('usePrintCommands: print-commands load failed', e)
        if (!cancelled) setCommands([])
      })
    return () => {
      cancelled = true
    }
  }, [connectionId, objectName, recordId])

  const hasPrintLayouts = commands.length > 0

  const runPrint = useCallback(
    async (record: Record<string, unknown>, command: PrintCommand) => {
      if (!connectionId || printLoading || !objectName) return
      const instanceId = String(record.id ?? record.ID ?? '')
      if (!instanceId) return

      setPrintLoading(true)
      try {
        const res = await runtimeApi.print(connectionId, objectName, {
          command_id: command.id,
          record_id: instanceId,
        })
        const payload = normalizePrintPayload(res)
        if (!payload) {
          throw new Error('Print response missing model')
        }
        const { openPrintWindow } = await import('./openPrintWindow')
        openPrintWindow({
          connectionId: String(connectionId),
          tableName: objectName,
          instanceId,
          layoutNodeId: command.layout_node_id || command.id,
          model: payload.model as FilledSpreadsheetModel,
          title: payload.title || command.title,
          locale: payload.locale,
          languages: payload.languages,
        })
      } catch (err) {
        logger.error('usePrintCommands runPrint:', err)
        await alertAsync(printErrorMessage(err))
      } finally {
        setPrintLoading(false)
      }
    },
    [connectionId, objectName, printLoading],
  )

  const openLayoutChoice = useCallback(
    (anchorEl: HTMLElement, record: Record<string, unknown>, mode: 'print' | 'pdf') => {
      if (!hasPrintLayouts) return
      // v1: print and pdf share one path (window hosts PDF export).
      if (commands.length === 1) {
        void runPrint(record, commands[0])
        return
      }
      setLayoutPickMenu({ anchorEl, record, mode })
    },
    [hasPrintLayouts, commands, runPrint],
  )

  return {
    commands,
    hasPrintLayouts,
    printLoading,
    runPrint,
    openLayoutChoice,
    layoutPickMenu,
    closeLayoutPickMenu: () => setLayoutPickMenu(null),
  }
}
